import React, { useState } from "react";
import { Plus, Check, Eye, EyeOff } from "lucide-react";
import useAsync from "../../utils/useAsync.js";
import { getProfile } from "../../utils/stockApi.js";
import { TABS, stockPath } from "../../utils/route.js";
import { loadWatchlists, saveWatchlists } from "../../utils/watchlists.js";
import { fmtMoney, fmtPct, fmtLongDate } from "../../utils/format.js";
import CompanySearch from "../CompanySearch.jsx";
import { Loading, SampleBanner } from "./common.jsx";
import OverviewTab from "./OverviewTab.jsx";
import WatchButton from "./WatchButton.jsx";
import FinancialsTab from "./FinancialsTab.jsx";
import ValuationTab from "./ValuationTab.jsx";
import ModelTab from "./ModelTab.jsx";
import EstimatesTab from "./EstimatesTab.jsx";
import TranscriptsTab from "./TranscriptsTab.jsx";
import FilingsTab from "./FilingsTab.jsx";
import OwnershipTab from "./OwnershipTab.jsx";
import useLiveQuote from "../../utils/useLiveQuote.js";

function OldWatchButton({ ticker, user, onRequireLogin }) {
  const [, force] = useState(0);
  const data = loadWatchlists(user);
  const active = data.lists.find((l) => l.id === data.activeId) || data.lists[0];
  const inList = !!active && active.tickers.includes(ticker);

  const add = () => {
    if (!user) return onRequireLogin();
    let next = data;
    if (!active) {
      const id = Date.now().toString(36);
      next = { lists: [{ id, name: "My Watchlist", tickers: [ticker] }], activeId: id };
    } else {
      next = {
        ...data,
        lists: data.lists.map((l) =>
          l.id === active.id
            ? { ...l, tickers: l.tickers.includes(ticker) ? l.tickers.filter((t) => t !== ticker) : [...l.tickers, ticker] }
            : l
        ),
      };
    }
    saveWatchlists(user, next);
    force((n) => n + 1);
  };

  return (
    <button className={`btn ${inList ? "tool-btn" : "btn-purple"}`} onClick={add}>
      {inList ? <Check size={15} /> : <Plus size={15} />} {inList ? "In Watchlist" : "Add to Watchlist"}
    </button>
  );
}

export default function CompanyPage({ ticker, tab, user, onRequireLogin }) {
  const [showChart, setShowChart] = useState(true);
  const profile = useAsync(() => getProfile(ticker), [ticker]);
  const quote = useLiveQuote(ticker);
  const known = profile.loading || profile.error || profile.data !== null;

  if (!known) {
    return (
      <div className="co-page">
        <div className="card co-card co-empty">
          <h5>We couldn't find “{ticker}”</h5>
          <p>Check the ticker or search for the company by name.</p>
          <CompanySearch autoFocus />
        </div>
      </div>
    );
  }

  const q = quote.data;
  const p = profile.data;
  const up = q && q.change >= 0;

  let body;
  switch (tab) {
    case "financials": body = <FinancialsTab ticker={ticker} />; break;
    case "valuation": body = <ValuationTab ticker={ticker} />; break;
    case "model": body = <ModelTab ticker={ticker} user={user} onRequireLogin={onRequireLogin} />; break;
    case "estimates": body = <EstimatesTab ticker={ticker} />; break;
    case "transcripts": body = <TranscriptsTab ticker={ticker} />; break;
    case "filings": body = <FilingsTab ticker={ticker} />; break;
    case "ownership": body = <OwnershipTab ticker={ticker} />; break;
    default: body = <OverviewTab ticker={ticker} showChart={showChart} />;
  }

  return (
    <div className="co-page">
      <SampleBanner />

      {/* Stays at the top of the screen while you scroll */}
      <div className="co-sticky-bar">
        <div className="co-bar-top">
          <div className="co-bar-name">
            {p ? <><strong>{p.name}</strong><span className="co-exch">{p.exchange}:{p.ticker}</span></> : <Loading />}
          </div>
          {q && (
            <div className="co-bar-price">
              <b>{fmtMoney(q.price)}</b>
              <span className={up ? "change-up" : "change-down"}>
                {up ? "+" : ""}{q.change.toFixed(2)} ({fmtPct(q.changePct, 2, true)})
              </span>
              <small>{fmtLongDate(q.date)}</small>
            </div>
          )}
          <div className="co-actions">
            <WatchButton ticker={ticker} user={user} onRequireLogin={onRequireLogin} />
            <select className="co-currency" defaultValue="USD" aria-label="Currency" title="Only USD is available right now">
              <option value="USD">USD</option>
            </select>
            {tab === "overview" && (
              <button className="btn tool-btn" onClick={() => setShowChart(!showChart)}>
                {showChart ? <EyeOff size={15} /> : <Eye size={15} />} {showChart ? "Hide Chart" : "Show Chart"}
              </button>
            )}
          </div>
        </div>

        <nav className="co-tabs" aria-label="Company sections">
          {TABS.map((t) => (
            <a key={t.key} href={stockPath(ticker, t.key)} className={tab === t.key ? "active" : ""}>
              {t.label}
            </a>
          ))}
        </nav>
      </div>

      <div className="co-body">{body}</div>
    </div>
  );
}


